import { MuseumObject } from "@/backend/types/domain";
import * as React from "react";
import { ModalLayout } from "../layout";
import { ImageWithPlaceholder } from "./ImageWithPlaceholder";
import { artifactImageSources, formatSourceUrl } from "./MuseumObjectCard";
import { ShareButtons } from "./ShareButtons";
import { SimilarArtifacts } from "./SimilarArtifacts";
import { getCultureWikipediaUrl } from "../lib/cultureWikipedia";
import { useFilterContext } from "../context/FilterContext";
import { trackArtifactView, trackEvent } from "../lib/analytics";

const ENABLE_SIMILAR_ARTIFACTS = true;
// const ENABLE_SIMILAR_ARTIFACTS = typeof window !== "undefined" && window.location.hostname === "localhost";

export const MuseumObjectDetail: React.FC<{
  obj: MuseumObject;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}> = ({ obj, onClose, onPrev, onNext, hasPrev = false, hasNext = false }) => {
  const filterContext = useFilterContext();
  const detailContentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && hasPrev && onPrev) {
        onPrev();
      } else if (e.key === "ArrowRight" && hasNext && onNext) {
        onNext();
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasPrev, hasNext, onPrev, onNext, onClose]);

  // Keyed on the id, not the object: opening a card renders it once from the
  // grid and again when the hash listener refetches it, and that is one view.
  React.useEffect(() => {
    trackArtifactView(obj);
  }, [obj.id]);

  // Scroll to top when object changes
  React.useEffect(() => {
    if (detailContentRef.current) {
      detailContentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [obj.id]);

  return (
    <ModalLayout title={"DETAIL VIEW"} onClose={onClose}>
      <div
        ref={detailContentRef}
        className="museum-object-detail border border-theme-color p-4 space-y-3 backdrop-blur-md"
      >
        <h2
          id="object-detail-title"
          className="text-xl font-bold border-b-2 border-dashed border-theme-color pb-2"
        >
          {obj.title || "Untitled"}
        </h2>

        {/* Images section */}
        {obj.images && obj.images.length > 0 ? (
          <div className="flex flex-row gap-2 overflow-x-auto py-2">
            {obj.images
              .filter((img) => img.url)
              .map((img, idx) => (
                <ImageWithPlaceholder
                  key={idx}
                  {...artifactImageSources(obj, img)}
                  museum={obj.dataSource?.museum}
                  alt={obj.title || "Artifact image"}
                  className="h-[300px] md:h-[500px] object-contain border border-theme-color"
                />
              ))}
          </div>
        ) : null}

        {/* Core details */}
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
          {obj.artists?.length ? (
            <p>
              <span className="font-medium uppercase text-sm">Artists:</span>{" "}
              {obj.artists.map((artist) => artist.name).join(", ")}
            </p>
          ) : null}
          {obj.culture && (
            <p className="flex items-center gap-2 flex-wrap">
              <span className="font-medium uppercase text-sm">Culture:</span>{" "}
              {(() => {
                const wikiUrl = getCultureWikipediaUrl(obj.culture);
                return wikiUrl ? (
                  <a
                    href={wikiUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    {obj.culture} ↗
                  </a>
                ) : (
                  obj.culture
                );
              })()}
              {filterContext && (
                <button
                  onClick={() => {
                    trackEvent("filter_by_culture", {
                      culture: obj.culture!,
                      source: "detail",
                    });
                    filterContext.filterByCulture(obj.culture!);
                  }}
                  className="text-xs border border-theme-color px-1.5 py-0.5 hover:bg-theme-color"
                  title={`Show only "${obj.culture}" artifacts`}
                >
                  ⚲ FILTER
                </button>
              )}
            </p>
          )}
          {obj.date && (
            <p>
              <span className="font-medium uppercase text-sm">Date:</span>{" "}
              {obj.date.display}
              {obj.date.period && ` (${obj.date.period})`}
              {obj.date.century && ` - ${obj.date.century}`}
            </p>
          )}
          {obj.place && (
            <p>
              <span className="font-medium uppercase text-sm">Place:</span>{" "}
              {obj.place.name}
              {obj.place.country && `, ${obj.place.country}`}
              {obj.place.region && ` (${obj.place.region})`}
            </p>
          )}
          {obj.medium && (
            <p>
              <span className="font-medium uppercase text-sm">Medium:</span>{" "}
              {obj.medium}
            </p>
          )}
          {obj.materials && (
            <p>
              <span className="font-medium uppercase text-sm">Materials:</span>{" "}
              {obj.materials}
            </p>
          )}
          {obj.dimensions && (
            <p>
              <span className="font-medium uppercase text-sm">Dimensions:</span>{" "}
              {obj.dimensions.display}
            </p>
          )}
          {obj.classification && (
            <p>
              <span className="font-medium uppercase text-sm">
                Classification:
              </span>{" "}
              {obj.classification}
            </p>
          )}
          {obj.objectType && (
            <p>
              <span className="font-medium uppercase text-sm">
                Object Type:
              </span>{" "}
              {obj.objectType}
            </p>
          )}
          {obj.technique && obj.technique.length > 0 && (
            <p>
              <span className="font-medium uppercase text-sm">Technique:</span>{" "}
              {obj.technique.join(", ")}
            </p>
          )}
        </div>

        {/* Description sections */}
        {obj.physicalDescription && (
          <p>
            <span className="font-medium uppercase text-sm">
              Physical Description:
            </span>{" "}
            {obj.physicalDescription}
          </p>
        )}
        {obj.description && (
          <p>
            <span className="font-medium uppercase text-sm">Description:</span>{" "}
            {obj.description}
          </p>
        )}
        {obj.provenance && (
          <p>
            <span className="font-medium uppercase text-sm">Provenance:</span>{" "}
            {obj.provenance}
          </p>
        )}
        {obj.commentary && (
          <p>
            <span className="font-medium uppercase text-sm">Commentary:</span>{" "}
            {obj.commentary}
          </p>
        )}

        {/* Source and sharing */}
        <div className="border-t-2 border-dashed border-theme-color pt-3 mt-3">
          {obj.dataSource && (
            <p>
              <span className="font-medium uppercase text-sm">Source:</span>{" "}
              <a
                href={formatSourceUrl(obj)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-theme-color hover:underline"
              >
                {obj.dataSource.museum} &#8599;
              </a>
            </p>
          )}

          <ShareButtons obj={obj} />
        </div>

        {/* Museum information */}
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
          {obj.objectNumber && (
            <p>
              <span className="font-medium uppercase text-sm">
                Object Number:
              </span>{" "}
              {obj.objectNumber}
            </p>
          )}
          {obj.recordId && (
            <p>
              <span className="font-medium uppercase text-sm">Record ID:</span>{" "}
              {obj.recordId}
            </p>
          )}
          {obj.department && (
            <p>
              <span className="font-medium uppercase text-sm">Department:</span>{" "}
              {obj.department}
            </p>
          )}
          {obj.gallery && (
            <p>
              <span className="font-medium uppercase text-sm">Gallery:</span>{" "}
              {obj.gallery.location}
              {obj.gallery.number && ` (${obj.gallery.number})`}
              {obj.gallery.isOnView && " - Currently on view"}
            </p>
          )}
          {obj.creditLine && (
            <p>
              <span className="font-medium uppercase text-sm">
                Credit Line:
              </span>{" "}
              {obj.creditLine}
            </p>
          )}
          {obj.accessionYear && (
            <p>
              <span className="font-medium uppercase text-sm">
                Accession Year:
              </span>{" "}
              {obj.accessionYear}
            </p>
          )}
          {obj.rightsInformation && (
            <p>
              <span className="font-medium uppercase text-sm">Rights:</span>{" "}
              {obj.rightsInformation.statement}
              {obj.rightsInformation.usage &&
                ` - ${obj.rightsInformation.usage}`}
            </p>
          )}
        </div>

        {/* Lists: exhibitions, publications, related */}
        {obj.exhibitions && obj.exhibitions.length > 0 && (
          <div>
            <span className="font-medium uppercase text-sm">Exhibitions:</span>
            <ul className="list-disc list-inside ml-4 mt-1">
              {obj.exhibitions.map((exhibition, idx) => (
                <li key={idx}>
                  {exhibition.title}
                  {exhibition.venue && ` at ${exhibition.venue}`}
                  {exhibition.date && ` (${exhibition.date})`}
                </li>
              ))}
            </ul>
          </div>
        )}
        {obj.publications && obj.publications.length > 0 && (
          <div>
            <span className="font-medium uppercase text-sm">Publications:</span>
            <ul className="list-disc list-inside ml-4 mt-1">
              {obj.publications.map((publication, idx) => (
                <li key={idx}>
                  {publication.title}
                  {publication.author && ` by ${publication.author}`}
                  {publication.date && ` (${publication.date})`}
                  {publication.pageNumber && `, p. ${publication.pageNumber}`}
                </li>
              ))}
            </ul>
          </div>
        )}
        {obj.relatedObjects && obj.relatedObjects.length > 0 && (
          <div>
            <span className="font-medium uppercase text-sm">
              Related Objects:
            </span>
            <ul className="list-disc list-inside ml-4 mt-1">
              {obj.relatedObjects.map((related, idx) => (
                <li key={idx}>
                  {related.url ? (
                    <a
                      href={related.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-theme-color hover:underline"
                    >
                      {related.title}
                    </a>
                  ) : (
                    related.title
                  )}
                  {related.relationship && ` (${related.relationship})`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Similar Artifacts - only visible on localhost */}
        {obj.id && (
          <SimilarArtifacts
            artifactId={obj.id}
            enabled={ENABLE_SIMILAR_ARTIFACTS}
          />
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t-2 border-dashed border-theme-color">
          <button
            onClick={onPrev}
            disabled={!hasPrev}
            className={`border border-theme-color p-2 px-4 ${
              hasPrev
                ? "text-theme-color hover:bg-theme-color cursor-pointer"
                : "text-gray-400 border-gray-300 cursor-not-allowed"
            }`}
          >
            ← PREV
          </button>
          <span className="text-sm text-gray-500">
            Use ← → arrow keys to navigate
          </span>
          <button
            onClick={onNext}
            disabled={!hasNext}
            className={`border border-theme-color p-2 px-4 ${
              hasNext
                ? "text-theme-color hover:bg-theme-color cursor-pointer"
                : "text-gray-400 border-gray-300 cursor-not-allowed"
            }`}
          >
            NEXT →
          </button>
        </div>
      </div>
    </ModalLayout>
  );
};
